import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  ArrowRight,
  ArrowUpRight,
  WifiSlash,
  FilePdf,
  Calculator,
  Truck,
  UsersThree,
} from "@phosphor-icons/react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1563160305-82d7e7523b0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxyYW5jaCUyMHN1bnNldHxlbnwwfHx8fDE3ODU4NzUyNTV8MA&ixlib=rb-4.1.0&q=85",
  pasture:
    "https://images.unsplash.com/photo-1648197752056-86e65cbfe0cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxjYXR0bGUlMjBwYXN0dXJlfGVufDB8fHx8MTc4NTg3NTI1NXww&ixlib=rb-4.1.0&q=85",
  tablet:
    "https://images.unsplash.com/photo-1627666260660-812e4684a600?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxmYXJtZXIlMjBkaWdpdGFsJTIwdGFibGV0fGVufDB8fHx8MTc4NTg3NTI1NXww&ixlib=rb-4.1.0&q=85",
};

const EASE = [0.16, 1, 0.3, 1];

/* ---- kinetic masked line reveal ---- */
function MaskLine({ children, delay = 0 }) {
  return (
    <span className="reveal-mask">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Reveal({ children, delay = 0, y = 40, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

function Nav() {
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="landing-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-night-bg/90 backdrop-blur-md border-b border-night-line" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
          <img src="/logo-128.png" alt="GadoPro" className="h-10 w-10 rounded-md object-contain" />
          <div className="leading-tight">
            <div className="font-cabinet font-extrabold tracking-tight text-night-text text-lg">
              Gado<span className="text-gado-gold">Pro</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-night-sub/70">
              Gestão Inteligente de Pesagens
            </div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-[13px] tracking-wide text-night-sub">
          <a href="#manifesto" className="hover:text-night-text transition-colors">Manifesto</a>
          <a href="#recursos" className="hover:text-night-text transition-colors">Recursos</a>
          <a href="#numeros" className="hover:text-night-text transition-colors">Números</a>
        </nav>
        <Link
          to="/login"
          data-testid="landing-enter-button"
          className="group inline-flex items-center gap-2 rounded-full bg-gado-gold text-night-bg px-5 py-2.5 text-sm font-semibold hover:bg-gado-goldLight transition-colors"
        >
          Entrar
          <ArrowRight className="hover-arrow" size={16} weight="bold" />
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} className="relative h-[100svh] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img
          src={IMG.hero}
          alt="Fazenda ao entardecer"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 bg-night-bg"
        style={{ opacity: overlay }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-night-bg via-night-bg/20 to-transparent" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="uppercase text-[11px] md:text-xs tracking-[0.35em] text-gado-goldLight mb-6"
        >
          Gestão Inteligente de Pesagens
        </motion.p>
        <h1 className="font-cormorant font-light text-night-text leading-[0.92] tracking-tight text-[15vw] md:text-[8.5rem]">
          <MaskLine delay={0.15}>A balança</MaskLine>
          <MaskLine delay={0.3}>
            virou <span className="italic text-gado-gold">planilha.</span>
          </MaskLine>
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-night-sub text-base md:text-lg leading-relaxed">
            O formulário de compra de gado, digitalizado. Pese, calcule arrobas
            e frete, funcione offline no curral e gere o relatório em PDF na
            hora.
          </p>
          <Link
            to="/login"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-gado-gold text-night-bg px-8 py-4 font-semibold self-start whitespace-nowrap hover:bg-gado-goldLight transition-colors"
          >
            Começar agora
            <ArrowUpRight className="hover-arrow" size={20} weight="bold" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const words = ["Pesagem", "Arrobas", "Frete", "Offline-first", "Relatório PDF", "Parciais", "Compradores"];
  const line = [...words, ...words];
  return (
    <section className="border-y border-night-line py-8 overflow-hidden bg-night-bg">
      <div className="flex whitespace-nowrap animate-marquee">
        {line.map((w, i) => (
          <span
            key={i}
            className="font-cormorant italic text-night-sub/70 text-4xl md:text-6xl mx-8 flex items-center gap-8"
          >
            {w}
            <span className="text-gado-gold not-italic text-2xl">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

const CHAPTERS = [
  {
    n: "01",
    t: "Do papel ao pixel",
    d: "Aquele bloco de comercialização amassado no bolso vira um formulário digital completo: comprador, parceiro, fazenda, motorista, placa e ferro do gado.",
  },
  {
    n: "02",
    t: "A conta que nunca erra",
    d: "Peso do lote, quantidade, média, arrobas a @/30, desconto cedido em kg, valor por arroba e frete — tudo calculado na hora, sem calculadora de bolso.",
  },
  {
    n: "03",
    t: "Sinal? Não precisa.",
    d: "No meio do pasto não tem 4G. O GadoPro salva tudo no aparelho e sincroniza sozinho quando a conexão voltar. Nada se perde.",
  },
];

function Manifesto() {
  return (
    <section id="manifesto" className="bg-night-bg py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="uppercase text-xs tracking-[0.3em] text-gado-goldLight mb-16">
            Manifesto
          </p>
        </Reveal>
        <div className="space-y-20 md:space-y-28">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start border-t border-night-line pt-10">
                <div className="md:col-span-3">
                  <span className="font-cormorant text-7xl md:text-8xl text-gado-gold/90 leading-none">
                    {c.n}
                  </span>
                </div>
                <h3 className="md:col-span-4 font-cormorant text-3xl md:text-5xl text-night-text leading-tight">
                  {c.t}
                </h3>
                <p className="md:col-span-5 text-night-sub text-lg leading-relaxed md:pt-3">
                  {c.d}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, desc, className = "", img }) {
  return (
    <Reveal className={className}>
      <div className="group relative h-full overflow-hidden rounded-2xl border border-night-line bg-night-surface p-8 md:p-10">
        {img && (
          <div className="absolute inset-0 opacity-25 group-hover:opacity-35 transition-opacity duration-700">
            <img src={img} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-night-surface via-night-surface/70 to-transparent" />
          </div>
        )}
        <div className="relative">
          <span className="inline-grid place-items-center h-12 w-12 rounded-xl bg-gado-gold/15 text-gado-gold mb-6 ring-1 ring-gado-gold/25">
            <Icon size={24} weight="duotone" />
          </span>
          <h3 className="font-cabinet font-bold text-night-text text-xl md:text-2xl mb-3">
            {title}
          </h3>
          <p className="text-night-sub leading-relaxed max-w-md">{desc}</p>
        </div>
      </div>
    </Reveal>
  );
}

function Features() {
  return (
    <section id="recursos" className="bg-night-bg pb-28 md:pb-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="max-w-2xl mb-16">
            <p className="uppercase text-xs tracking-[0.3em] text-gado-goldLight mb-6">
              Recursos
            </p>
            <h2 className="font-cormorant text-4xl md:text-6xl text-night-text leading-tight">
              Feito para quem vive de <span className="italic text-gado-gold">gado</span>, não de sistema.
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
          <FeatureCard
            className="md:col-span-4"
            icon={Calculator}
            title="Cálculos automáticos"
            desc="Peso total do lote, quantidade, média por cabeça, arrobas a @/30, desconto cedido em kg e valor por arroba. O total sai pronto, sem erro de conta."
            img={IMG.pasture}
          />
          <FeatureCard
            className="md:col-span-2"
            icon={WifiSlash}
            title="Offline-first"
            desc="Registre no curral sem internet. Sincroniza sozinho quando o sinal voltar."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={FilePdf}
            title="Relatório em PDF"
            desc="Documento profissional com seu logotipo, dados da empresa e todos os lançamentos."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={Truck}
            title="Transporte & frete"
            desc="KM inicial e final, valor do km e frete calculado — tudo dentro da pesagem."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={UsersThree}
            title="Cadastros & parciais"
            desc="Compradores, parceiros e fazendas salvos. Pesagens parciais de várias fazendas numa transação."
            img={IMG.tablet}
          />
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    { k: "@/30", v: "Arroba exata" },
    { k: "100%", v: "Offline no curral" },
    { k: "1 toque", v: "Do lançamento ao PDF" },
    { k: "365 dias", v: "Sessão sem re-login" },
  ];
  return (
    <section id="numeros" className="bg-night-surface py-24 border-y border-night-line">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-6">
        {stats.map((s, i) => (
          <Reveal key={s.k} delay={i * 0.05}>
            <div>
              <div className="font-cormorant text-5xl md:text-7xl text-gado-gold leading-none mb-3">
                {s.k}
              </div>
              <div className="text-night-sub text-sm md:text-base tracking-wide">
                {s.v}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative bg-night-bg py-32 md:py-48 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
        <Reveal>
          <h2 className="font-cormorant text-5xl md:text-8xl text-night-text leading-[0.95] mb-10">
            Sua próxima compra
            <br />
            <span className="italic text-gado-gold">começa aqui.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/login"
            data-testid="cta-bottom-button"
            className="group inline-flex items-center gap-3 rounded-full bg-gado-gold text-night-bg px-10 py-5 text-lg font-semibold hover:bg-gado-goldLight transition-colors"
          >
            Criar minha conta
            <ArrowUpRight className="hover-arrow" size={22} weight="bold" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-night-bg border-t border-night-line py-12">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/logo-128.png" alt="GadoPro" className="h-9 w-9 rounded-md object-contain" />
          <div className="leading-tight">
            <div className="font-cabinet font-extrabold text-night-text">
              Gado<span className="text-gado-gold">Pro</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-night-sub/70">
              Gestão Inteligente de Pesagens
            </div>
          </div>
        </div>
        <p className="text-night-sub text-sm">
          © {new Date().getFullYear()} GadoPro · Gestão de pesagens de gado
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="marketing-root grain min-h-screen">
      <Nav />
      <Hero />
      <Marquee />
      <Manifesto />
      <Features />
      <Stats />
      <CTA />
      <Footer />
    </div>
  );
}
